import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatNumber } from "src/utils/numberFormat";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  METRIC_COLORS,
  OrganicChartTooltip,
  type MetricKey,
  type TooltipValue,
  type TrendGranularity,
} from "./googleAdsChartConfig";
import type { GoogleSectionHelpKey } from "./googleAdsTableConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

interface GooglePerformanceTrendsPanelProps {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  trendGranularity: TrendGranularity;
  setTrendGranularity: Dispatch<SetStateAction<TrendGranularity>>;
  activeMetrics: MetricKey[];
  toggleMetric: (_metric: MetricKey) => void;
  paginatedTrendData: Array<{
    label: string;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
  }>;
  hasSinglePerformanceTrendPoint: boolean;
  formatDateShortChart: (_value: string) => string;
  formatChartAxisCompactLocalized: (_value: unknown) => string;
  formatChartMetricTooltipValue: (_value: TooltipValue, _metricKey: string) => string;
  currentTrendPage: number;
  setCurrentTrendPage: Dispatch<SetStateAction<number>>;
  trendTotalPages: number;
}

const GooglePerformanceTrendsPanel: FC<GooglePerformanceTrendsPanelProps> = ({
  googleSectionHelp,
  trendGranularity,
  setTrendGranularity,
  activeMetrics,
  toggleMetric,
  paginatedTrendData,
  hasSinglePerformanceTrendPoint,
  formatDateShortChart,
  formatChartAxisCompactLocalized,
  formatChartMetricTooltipValue,
  currentTrendPage,
  setCurrentTrendPage,
  trendTotalPages,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  // "Back" points towards the start of the reading direction, which is the right edge in Arabic.
  const PreviousIcon = chartRtl.isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = chartRtl.isRTL ? ChevronLeft : ChevronRight;
  // Page counters are built client-side, so their digits have to be localized here — the rest of
  // the panel's numbers arrive pre-formatted from the API.
  const paginationLabel = t("pagination.pageOf", {
    current: formatNumber(currentTrendPage, {}, i18n.language),
    total: formatNumber(trendTotalPages, {}, i18n.language),
  });

  return (
    <article className="paid-media-panel paid-media-panel--trend">
      <div className="paid-media-panel__header">
        <div className="paid-media-panel__heading">
          <h3>{t("googleAdsReport.sections.performanceTrends")}</h3>
          <DefinitionTooltip
            label={t("googleAdsReport.sections.performanceTrends")}
            info={googleSectionHelp("performanceTrends")}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
        <div className="paid-media-panel__header-actions">
          <div
            className="paid-media-trend-granularity"
            role="group"
            aria-label={t("googleAdsReport.trends.granularityAria")}
          >
            {(["daily", "monthly"] as const).map(option => (
              <button
                key={option}
                type="button"
                className={
                  trendGranularity === option
                    ? "paid-media-trend-granularity__btn paid-media-trend-granularity__btn--active"
                    : "paid-media-trend-granularity__btn"
                }
                onClick={() => setTrendGranularity(option)}
              >
                {t(`googleAdsReport.trends.${option}`)}
              </button>
            ))}
          </div>
          <div className="paid-media-trend-chips">
            {Object.keys(METRIC_COLORS).map(metric => (
              <button
                key={metric}
                type="button"
                className={
                  activeMetrics.includes(metric as MetricKey)
                    ? "paid-media-trend-chips__item paid-media-trend-chips__item--active"
                    : "paid-media-trend-chips__item"
                }
                onClick={() => toggleMetric(metric as MetricKey)}
              >
                <span
                  className="paid-media-trend-chips__dot"
                  style={{ backgroundColor: METRIC_COLORS[metric as MetricKey] }}
                />
                {t(`googleAdsReport.metrics.${metric as MetricKey}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="paid-media-chart paid-media-chart--trend">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={paginatedTrendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="label"
              reversed={chartRtl.axisReversed}
              tick={{ fill: "#64748b", fontSize: 12 }}
              tickFormatter={formatDateShortChart}
            />
            <YAxis
              yAxisId="spend"
              orientation={chartRtl.yAxisOrientation}
              tick={{ fill: "#64748b", fontSize: 12 }}
              tickFormatter={formatChartAxisCompactLocalized}
            />
            <YAxis
              yAxisId="count"
              orientation={chartRtl.oppositeYAxisOrientation}
              tick={{ fill: "#64748b", fontSize: 12 }}
              tickFormatter={formatChartAxisCompactLocalized}
            />
            <Tooltip
              content={({ active, label, payload }) => (
                <OrganicChartTooltip
                  active={active}
                  label={formatDateShortChart(String(label ?? ""))}
                  labelTitle={t("googleAdsReport.chart.date")}
                  payload={payload}
                  seriesLabelForDataKey={key =>
                    t(`googleAdsReport.metrics.${key as MetricKey}`)
                  }
                  valueFormatter={formatChartMetricTooltipValue}
                />
              )}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            {activeMetrics.map(metric => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                name={metric}
                yAxisId={metric === "spend" ? "spend" : "count"}
                stroke={METRIC_COLORS[metric]}
                strokeWidth={2.4}
                dot={hasSinglePerformanceTrendPoint ? { r: 4 } : false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div
        className="paid-media-trend-pagination"
        role="navigation"
        aria-label={paginationLabel}
      >
        <span className="paid-media-trend-pagination__label">{paginationLabel}</span>
        <div className="paid-media-trend-pagination__nav">
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentTrendPage <= 1}
            onClick={() => setCurrentTrendPage(page => Math.max(1, page - 1))}
            aria-label={t("pagination.previousPage")}
          >
            <PreviousIcon size={16} aria-hidden />
          </button>
          <button
            type="button"
            className="paid-media-trend-pagination__btn"
            disabled={currentTrendPage >= trendTotalPages}
            onClick={() =>
              setCurrentTrendPage(page => Math.min(trendTotalPages, page + 1))
            }
            aria-label={t("pagination.nextPage")}
          >
            <NextIcon size={16} aria-hidden />
          </button>
        </div>
      </div>
    </article>
  );
};

export default GooglePerformanceTrendsPanel;
